import React, { useMemo, useRef, useState } from 'react';
import { applyTemplate, createTargetGroup, deleteTemplate, ensureTargetGroupLinked, errorLines, listCountries, listTemplates } from '../../api';
import { useAsyncData } from '../../hooks/useAsyncData';
import type { Project, TargetGroupTemplate } from '../../types';
import { Badge, Button, EmptyState, ErrorState, InlineAlert, Modal, Skeleton, cx, useToast } from '../../ui';
import { useAuth } from '../auth/AuthProvider';
import { summarise, templateParts } from './templateInfo';

interface TemplatePickerDialogProps {
  open: boolean;
  project: Project;
  onClose: () => void;
  /** open a target group in the wizard; `fromTemplate` is true when a template was applied to it */
  onOpenTargetGroup: (targetGroupId: string, options: { fromTemplate: boolean }) => void;
}

/**
 * "Start from template": pick a template, then the target group is created and the template applied to it.
 * Templates shown are the company-wide ones plus the signed-in user's client's own (GET ?clientId=).
 * Applying is checked first by the server: on a problem it answers 400 with `errors` and changes nothing. The new
 * (still empty) target group exists by then, so the dialog offers to open it instead of losing it.
 */
export function TemplatePickerDialog({ open, project, onClose, onOpenTargetGroup }: TemplatePickerDialogProps) {
  const { user } = useAuth();
  const { notify } = useToast();
  const clientId = user && typeof user.clientId === 'string' && user.clientId ? user.clientId : undefined;
  const templates = useAsyncData<TargetGroupTemplate[]>((signal) => listTemplates(clientId, { signal }), [clientId, open], { enabled: open });
  const countries = useAsyncData<{ country_id: string | number; name: string }[]>((signal) => listCountries({ signal }), [], { enabled: open });
  const countryNames = useMemo(() => Object.fromEntries((countries.data || []).map((c) => [String(c.country_id), c.name])), [countries.data]);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [problems, setProblems] = useState<string[]>([]);
  const [emptyGroupId, setEmptyGroupId] = useState<string | null>(null); // created, but the template did not apply
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);

  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) { setSelectedId(null); setConfirmDelete(null); setProblems([]); setEmptyGroupId(null); }
  }

  const selected = (templates.data || []).find((t) => t.id === selectedId) || null;

  const start = async () => {
    if (!selected || busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setProblems([]);
    let groupId: string | null = null;
    try {
      const group = await createTargetGroup(project.id, { name: `Target group ${(project.targetGroups || []).length + 1}` });
      groupId = group.id;
      await ensureTargetGroupLinked(project.id, group.id);
      await applyTemplate(project.id, group.id, selected.id);
      notify(`Started from “${selected.name}”`, { tone: 'ok' });
      onClose();
      onOpenTargetGroup(group.id, { fromTemplate: true });
    } catch (err) {
      setProblems(errorLines(err));
      if (groupId) setEmptyGroupId(groupId); // the group was created: let the user open it rather than lose it
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  const remove = async (id: string) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    try {
      await deleteTemplate(id);
      notify('Template deleted', { tone: 'ok' });
      setConfirmDelete(null);
      if (selectedId === id) setSelectedId(null);
      templates.reload();
    } catch (err) {
      setProblems(errorLines(err));
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  const scopeBadge = (t: TargetGroupTemplate) => (t.clientId == null ? <Badge tone="neutral">Company-wide</Badge> : <Badge tone="brand">{t.clientId === clientId ? 'My client' : 'Client'}</Badge>);

  return (
    <Modal
      open={open}
      size="lg"
      title="Start from template"
      subtitle="Creates a target group with the template's settings. You can change everything afterwards."
      onClose={() => { if (!busyRef.current) onClose(); }}
      footer={(
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={start} loading={busy} disabled={!selected}>Use template</Button>
        </>
      )}
    >
      {templates.loading && !templates.data ? (
        <div className="space-y-2" aria-busy="true" aria-label="Loading templates">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-16 w-full" />)}</div>
      ) : templates.error ? (
        <ErrorState title="Could not load templates" message={errorLines(templates.error).join(' ')} onRetry={templates.reload} />
      ) : (templates.data || []).length === 0 ? (
        <EmptyState title="No templates yet" description="Use “Save as template” on a finished target group to create one." />
      ) : (
        <ul role="radiogroup" aria-label="Templates" className="space-y-2">
          {(templates.data || []).map((t) => {
            const on = t.id === selectedId;
            const what = templateParts(t);
            const summary = summarise(t, t.basicSetting ? countryNames[String(t.basicSetting.country_id)] : undefined);
            return (
              <li key={t.id} className={cx('rounded-lg border px-3.5 py-3', on ? 'border-brand-300 bg-brand-50/50' : 'border-line')}>
                <div className="flex items-start gap-3">
                  <input type="radio" name="template" aria-label={t.name} className="mt-1 h-4 w-4 accent-brand-600" checked={on} onChange={() => setSelectedId(t.id)} />
                  <div className="min-w-0 flex-1">
                    <p className="flex flex-wrap items-center gap-2 text-sm font-semibold text-ink">{t.name} {scopeBadge(t)}</p>
                    {t.description && <p className="mt-0.5 text-sm text-muted">{t.description}</p>}
                    {summary && <p className="mt-0.5 text-xs text-muted">{summary}</p>}
                    {what.length > 0 && <p className="mt-1.5 flex flex-wrap gap-1.5">{what.map((w) => <span key={w} className="rounded-full bg-chip px-2 py-0.5 text-[11px] font-semibold text-muted">{w}</span>)}</p>}
                  </div>
                  {confirmDelete === t.id ? (
                    <span className="flex flex-none items-center gap-1.5 text-xs">
                      <span className="text-muted">Delete for everyone who can see it?</span>
                      <Button variant="danger" size="sm" onClick={() => remove(t.id)} disabled={busy}>Delete</Button>
                      <Button variant="ghost" size="sm" onClick={() => setConfirmDelete(null)} disabled={busy}>Keep</Button>
                    </span>
                  ) : (
                    <Button variant="ghost" size="sm" aria-label={`Delete ${t.name}`} onClick={() => setConfirmDelete(t.id)} disabled={busy}>Delete</Button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {problems.length > 0 && (
        <div className="mt-4">
          <InlineAlert>{problems.length === 1 ? problems[0] : <><b>{problems[0]}</b><ul className="ml-4 mt-1 list-disc">{problems.slice(1).map((m) => <li key={m}>{m}</li>)}</ul></>}</InlineAlert>
          {emptyGroupId && (
            <p className="mt-2 text-sm text-muted">
              The target group was created, but the template was not applied.{' '}
              <button type="button" className="font-semibold text-brand-700 hover:underline" onClick={() => { onClose(); onOpenTargetGroup(emptyGroupId, { fromTemplate: false }); }}>Open the empty target group</button>
            </p>
          )}
        </div>
      )}
    </Modal>
  );
}
